import { formatPrice } from "../../../utils/helpers";
import { chartDateOptions, chartDateTimeFrames, chartTypes } from "./chartFilterEnums";
import { biEnvironments, chartFilterKeys, reporthubChartOptionKeys, reportSubtypeKeys, reportTypeOptionKeys } from "./chartOptions";
import { WarehouseLocationOccupanceDateTimeCategory } from "./components/reports/RhWarehouseLocations/WarehouseLocationOccupanceDateTimeCategory";
import { pivotSettings } from "./reportUtils";

export const reportTypes = [
    reportTypeOptionKeys.PIVOTTABLE,
    reportTypeOptionKeys.WITHCHART
];

// A report's filter list holds two kinds of entry:
//
//   "shipper"
//       shown in every environment
//
//   {
//       key: "relations",
//       environments: ["fulfilment"],
//   }
//       shown only in the environments it lists
//
// Given one environment this returns the filter keys that environment may see. `filters` and
// `pivotTableOptions` are resolved separately, so a filter can be offered as a pivot axis
// without also being offered as a filter.
export const getFiltersForEnvironment = (reportFilters, environment) => {
    const visibleFilters = [];

    for (const reportFilter of reportFilters || []) {
        if (typeof reportFilter === "string") {
            visibleFilters.push(reportFilter);
            continue;
        }

        if (reportFilter.environments.includes(environment)) {
            visibleFilters.push(reportFilter.key);
        }
    }

    return visibleFilters;
};

export const reporthubChartOptions = [
    {
        name: reporthubChartOptionKeys.ORDERS,
        // A subtype decides what a row of this report counts: the entity itself (`base`), its lines
        // (`lines`), or the amounts on those lines summed (`products`). The first entry is what a report
        // gets when it is created; a report config without `subtypes` counts the entity and offers no
        // toggle. `labelKey` is the translation key each option is shown with.
        subtypes: [
            { key: reportSubtypeKeys.BASE, labelKey: "orders" },
            { key: reportSubtypeKeys.LINES, labelKey: "orderlines" },
            { key: reportSubtypeKeys.PRODUCTS, labelKey: "products" },
        ],
        allowedDisplayTypes:[
            chartTypes.BAR,
            chartTypes.LINE,
            chartTypes.AREA,
        ],
        filters: [
            chartFilterKeys.SHIPPER,
            {
                key: chartFilterKeys.SHIPPERPROFILES,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            {
                key: chartFilterKeys.STREAM,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            chartFilterKeys.COUNTRIES,
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            {
                key: chartFilterKeys.WEBSHOPCREDENTIALS,
                environments: [biEnvironments.WAREHOUSE, biEnvironments.RELATION],
            },
        ],
        pivotTableOptions:[
            chartFilterKeys.SHIPPER,
            {
                key: chartFilterKeys.SHIPPERPROFILES,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            {
                key: chartFilterKeys.STREAM,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            chartFilterKeys.COUNTRIES,
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            {
                key: chartFilterKeys.WEBSHOPCREDENTIALS,
                environments: [biEnvironments.WAREHOUSE, biEnvironments.RELATION],
            },
            // Axis only: rows per product, read off the order lines.
            chartFilterKeys.PRODUCTS,
        ],
        // pivotHidden: [
        //     pivotSettings.HEATMAP,
        //     pivotSettings.TRENDS
        // ]
    },
    {
        name: reporthubChartOptionKeys.INBOUNDS,
        // Not offered to relations for now: a relation builds reports on its own orders, returns and
        // shipments; receiving work stays the warehouse's to report on. The backend enforces the same set.
        environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
        subtypes: [
            { key: reportSubtypeKeys.BASE, labelKey: "inbound" },
            { key: reportSubtypeKeys.LINES, labelKey: "inboundlines" },
            { key: reportSubtypeKeys.PRODUCTS, labelKey: "products" },
        ],
        allowedDisplayTypes:[
            chartTypes.BAR,
            chartTypes.LINE,
            chartTypes.AREA,
        ],
        filters: [
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.PRODUCTS,
        ],
        pivotTableOptions:[
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.PRODUCTS,
        ],
    },
    {
        name: reporthubChartOptionKeys.RETURNS,
        subtypes: [
            { key: reportSubtypeKeys.BASE, labelKey: "return" },
            { key: reportSubtypeKeys.LINES, labelKey: "returnlines" },
            { key: reportSubtypeKeys.PRODUCTS, labelKey: "products" },
        ],
        allowedDisplayTypes:[
            chartTypes.BAR,
            chartTypes.LINE,
            chartTypes.AREA,
        ],
        filters: [
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.TYPE,
            chartFilterKeys.WEBSHOPCREDENTIALS,
            chartFilterKeys.WEBSHOP_TYPE,
        ],
        pivotTableOptions:[
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.TYPE,
            chartFilterKeys.WEBSHOPCREDENTIALS,
            chartFilterKeys.WEBSHOP_TYPE,
        ],
    },
    {
        name: reporthubChartOptionKeys.SHIPMENTS,
        allowedDisplayTypes:[
            chartTypes.BAR,
            chartTypes.LINE,
            chartTypes.AREA,
        ],
        filters: [
            {
                key: chartFilterKeys.STREAM,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            chartFilterKeys.SHIPPER,
            {
                key: chartFilterKeys.SHIPPERPROFILES,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            chartFilterKeys.COUNTRIES,
            {
                // Packaging is the warehouse's own concern, so the relation environment never sees it.
                key: chartFilterKeys.CARRIERS,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            {
                key: chartFilterKeys.WEBSHOPCREDENTIALS,
                environments: [biEnvironments.WAREHOUSE, biEnvironments.RELATION],
            },
        ],
        pivotTableOptions:[
            {
                key: chartFilterKeys.STREAM,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            chartFilterKeys.SHIPPER,
            {
                key: chartFilterKeys.SHIPPERPROFILES,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            chartFilterKeys.COUNTRIES,
            {
                key: chartFilterKeys.CARRIERS,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            {
                key: chartFilterKeys.WEBSHOPCREDENTIALS,
                environments: [biEnvironments.WAREHOUSE, biEnvironments.RELATION],
            },
        ],
    },
    {
        // Reads the same shipments as the SHIPMENTS report: `sourceType` is what the backend is asked
        // for, and its filters and dimensions are inherited, so the lists below are additions — not a
        // redefinition. It stands apart from SHIPMENTS only so the frontend can tell the two reports
        // apart and explain what a scan is, which would be noise on a report whose columns are arbitrary.
        //
        // Not offered to relations: a parcel that never reached the carrier is the warehouse's to chase.
        name: reporthubChartOptionKeys.SCANSTATUS,
        sourceType: reporthubChartOptionKeys.SHIPMENTS,
        environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
        // The report is named after what it is, not after the dimension it is built on: `scanstatus`
        // is the column header and the filter label, and a report title reading "Scanstatus" says
        // nothing about it being shipments. Saved reports still group under shipments in the sidebar,
        // because that grouping reads the backend `type`.
        labelKey: "scanstatus_report",
        // Declared rather than inherited: the report picker reads this straight off the raw option,
        // before sourceType is resolved.
        //
        // Bar only. The report is its dimension, and a category is what carries a dimension onto a
        // chart — which only the bar chart draws. A line or area chart has a date axis and nothing to
        // split it by, so it would plot a plain shipment count under a scanstatus name.
        allowedDisplayTypes:[
            chartTypes.BAR,
        ],
        filters: [
            chartFilterKeys.SCANSTATUS,
            // Only offered here, not on the shipments report: the user behind a shipment is read out of
            // the order's shipment log, one lookup per row, and the question it answers — whose labels
            // never got scanned — belongs to this report.
            {
                key: chartFilterKeys.USERS,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
        ],
        // The lists are additive through `sourceType`, so the rows keep every shipments dimension —
        // relatie, vervoerder, stream, land — and only add user. Scanstatus is deliberately not among
        // them: scanstatus against scanstatus says nothing, scanstatus against these is the point.
        pivotTableOptions: [
            {
                key: chartFilterKeys.USERS,
                environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
            },
        ],
        // Scanstatus is the column, always. Choosing this report is choosing that, so the column is
        // seeded with it and cannot be swapped for another dimension or for a date range — the coloured
        // headers that explain each bucket only hold while scanstatus is what the columns are.
        fixedPivotColumn: chartFilterKeys.SCANSTATUS,
        // Every pivot mode reads the columns as a period — this period against the previous one, against
        // last year, against each other. These columns are scan buckets, so there is no earlier column to
        // compare against and the modes would only restate the same four numbers. The toggle is hidden,
        // which leaves the report in the normal view and, through `modeLocked`, exports one sheet.
        pivotHidden: [
            pivotSettings.MODE,
        ],
    },
    {
        name: reporthubChartOptionKeys.FINANCIAL,
        // Invoicing only means something to a fulfilment warehouse billing its relations, and to the
        // relation being billed — "how much was I invoiced on a given day". A warehouse holding its own
        // stock is never invoiced through this system, so the report is hidden there. The relation side
        // is limited to orders, returns and shipments for now, so it does not see this report either;
        // the backend enforces the same set.
        environments: [biEnvironments.FULFILMENT],
        allowedDisplayTypes:[
            chartTypes.BAR,
            chartTypes.LINE,
            chartTypes.AREA,
        ],
        filters: [
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.INVOICEPRODUCTS,
            {
                // The categories are the warehouse's own billing taxonomy, so only the fulfilment
                // warehouse that maintains them reports on them.
                key: chartFilterKeys.INVOICEPRODUCTCATEGORIES,
                environments: [biEnvironments.FULFILMENT],
            },
        ],
        pivotTableOptions: [
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.INVOICEPRODUCTS,
            {
                key: chartFilterKeys.INVOICEPRODUCTCATEGORIES,
                environments: [biEnvironments.FULFILMENT],
            },
        ],
        chart: {
            labelFormat: {
                dataLabels: (i18n,val) => {
                    return formatPrice(i18n,val);
                },
                yaxis: (i18n,val) => {
                    return formatPrice(i18n,val);
                },
            }
        },
    },
    {
        name: reporthubChartOptionKeys.WAREHOUSELOCATIONS,
        // Not offered to relations for now.
        environments: [biEnvironments.FULFILMENT, biEnvironments.WAREHOUSE],
        allowedDisplayTypes:[
            chartTypes.BAR,
            chartTypes.LINE,
            chartTypes.AREA,
        ],
        filters: [
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.TYPE,
            chartFilterKeys.WAREHOUSE_ZONE,
            chartFilterKeys.LOCATION_OCCUPIED,
        ],
        pivotTableOptions: [
            {
                key: chartFilterKeys.RELATIONS,
                environments: [biEnvironments.FULFILMENT],
            },
            chartFilterKeys.TYPE,
            chartFilterKeys.WAREHOUSE_ZONE,
        ],
        categorySettings : () => {
            return {
                defaultDateTime:{
                    ignoreDateOnSave: true,
                    custom: true,
                    type: chartDateOptions.RELATIVE,
                    frame: chartDateTimeFrames.ONEDAY,
                    days:1,
                    component: WarehouseLocationOccupanceDateTimeCategory,
                },
            }
        }
    },
]

// The reports one environment may build. A report without an `environments` list is available in all
// of them; only a report that names its environments is restricted.
export const getReportsForEnvironment = (environment) => {
    const visibleReports = [];

    for (const report of reporthubChartOptions) {
        if (!report.environments || report.environments.includes(environment)) {
            visibleReports.push(report);
        }
    }

    return visibleReports;
};

// Looks a report up by type and hands back its config with both filter lists already narrowed to one
// environment. Callers keep reading `.filters` and `.pivotTableOptions` as plain key arrays and never
// have to know that an entry can be tagged.
//
// A report built on another report's data (`sourceType`) starts from that report's lists and appends
// its own, so it never has to restate the dimensions it shares — it only names what it adds.
export const getReportForEnvironment = (reportType, environment) => {
    const report = reporthubChartOptions.find(option => option.name === reportType);

    if (!report) {
        return undefined;
    }

    const source = report.sourceType
        ? reporthubChartOptions.find(option => option.name === report.sourceType)
        : null;

    const inherit = (key) => [...(source?.[key] ?? []), ...(report[key] ?? [])];

    return {
        ...report,
        allowedDisplayTypes: report.allowedDisplayTypes ?? source?.allowedDisplayTypes,
        filters: getFiltersForEnvironment(inherit("filters"), environment),
        pivotTableOptions: getFiltersForEnvironment(inherit("pivotTableOptions"), environment),
    };
};

// The type the backend is asked for. Most reports are their own source; a report that only differs in
// how the frontend presents it names the report it borrows its data from via `sourceType`.
export const getSourceType = (reportType) => {
    const report = reporthubChartOptions.find(option => option.name === reportType);
    return report?.sourceType ?? reportType;
};

// The translation key a report is titled with. Most reports are named by their own key; a report that
// borrows another's data names itself, so the title says which report is open rather than which data
// it sits on.
export const getReportLabelKey = (reportType) => {
    const report = reporthubChartOptions.find(option => option.name === reportType);
    return report?.labelKey ?? reportType;
};

// Which report config a saved report should be read with. `type` is what the backend stores and works
// with, so two reports sharing a source are indistinguishable by it — `reportKey` is what keeps them
// apart. Reports saved before `reportKey` existed have none, and fall back to `type` unchanged.
export const getReportKey = (currentReport) => currentReport?.reportKey ?? currentReport?.type;

// The subtypes a report may switch between, read off its config. Empty for a report whose config
// offers none, so callers can render a toggle without first checking the report type.
export const getReportSubtypes = (currentReport, environment) =>
    getReportForEnvironment(getReportKey(currentReport), environment)?.subtypes ?? [];